// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import debounce from "lodash/debounce";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

const ACTIVITY_KEY = "user-activity-timestamp";
const LOGOUT_KEY = "force-logout";
const MODAL_CLOSED_KEY = "user-modal-closed";

interface Props {
    sessionTimeout: number;
    modalWarningTime: number;
    onTimeout: () => void;
}

const events = ["mousemove", "keydown", "mousedown", "touchstart"];

export function useSyncedInactivityTimer({ sessionTimeout, modalWarningTime, onTimeout }: Props) {
    const [isWarningModalVisible, setWarningModalVisible] = useState(false);

    const isModalVisibleRef = useRef(false);
    const timeoutRef = useRef<number | null>(null);
    const warningTimeoutRef = useRef<number | null>(null);

    const clearAllTimers = () => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }
        if (warningTimeoutRef.current) {
            clearTimeout(warningTimeoutRef.current);
        }
    };

    const startTimers = (hideModal?: boolean) => {
        clearAllTimers();
        localStorage.setItem(ACTIVITY_KEY, String(Date.now()));

        if (hideModal) {
            setWarningModalVisible(false);
            isModalVisibleRef.current = false;
        }

        warningTimeoutRef.current = window.setTimeout(() => {
            setWarningModalVisible(true);
            isModalVisibleRef.current = true;
        }, sessionTimeout - modalWarningTime);

        timeoutRef.current = window.setTimeout(() => {
            localStorage.removeItem(ACTIVITY_KEY);
            localStorage.removeItem(MODAL_CLOSED_KEY);
            localStorage.setItem(LOGOUT_KEY, String(Date.now()));
            onTimeout?.();
        }, sessionTimeout);
    };

    const handleActivity = useCallback(() => {
        if (!isModalVisibleRef.current) {
            startTimers();
        }
    }, [startTimers]);

    const debouncedHandleActivity = useMemo(() => debounce(handleActivity, 1000), [handleActivity]);

    const resumeFromModal = () => {
        isModalVisibleRef.current = false;
        setWarningModalVisible(false);
        localStorage.setItem(MODAL_CLOSED_KEY, String(Date.now()));
        attachActivityListeners();
        startTimers();
    };

    const attachActivityListeners = useCallback(() => {
        events.forEach(event => window.addEventListener(event, debouncedHandleActivity));
    }, [debouncedHandleActivity]);

    const detachActivityListeners = useCallback(() => {
        events.forEach(event => window.removeEventListener(event, debouncedHandleActivity));
    }, [debouncedHandleActivity]);

    useEffect(() => {
        startTimers();
        attachActivityListeners();

        const handleStorage = (e: StorageEvent) => {
            if (e.key === ACTIVITY_KEY && !isModalVisibleRef.current) {
                startTimers();
            }

            if (e.key === LOGOUT_KEY) {
                onTimeout?.();
            }

            if (e.key === MODAL_CLOSED_KEY) {
                setWarningModalVisible(false);
                isModalVisibleRef.current = false;
                startTimers();
            }
        };

        window.addEventListener("storage", handleStorage);

        return () => {
            clearAllTimers();
            detachActivityListeners();
            window.removeEventListener("storage", handleStorage);
        };
    }, []);

    useEffect(() => {
        isModalVisibleRef.current = isWarningModalVisible;

        if (isWarningModalVisible) {
            detachActivityListeners();
        } else {
            attachActivityListeners();
        }
    }, [isWarningModalVisible, attachActivityListeners, detachActivityListeners]);

    return { isWarningModalVisible, resumeFromModal };
}
